/*
 * @Author: Auto
 * @Date: 2025-01-27
 * @Description: 小微信贷模块主页面
 */
import React, { useEffect, useState } from 'react'
import { Button, message, Pagination } from 'antd'
import styles from './index.scss'
import Loan from '@/modules/loan'
import {
  PRODUCT_ENUM_GROUPS,
  buildProductListRequest,
  normalizeProductCard
} from './loanAdapter'

const CUSTOMER_TYPE_OPTIONS = PRODUCT_ENUM_GROUPS.customerTypes.filter(option => ['0', '1'].includes(option.phjrCd))
const ACCEPT_MODE_OPTIONS = PRODUCT_ENUM_GROUPS.acceptModes.filter(option => ['0', '1'].includes(option.phjrCd))
const [LOAN_TERMS_SORT, INTEREST_RATES_SORT, CREDIT_AMOUNT_SORT] = PRODUCT_ENUM_GROUPS.productSorts
const PRODUCT_SORT_OPTIONS = [
  { label: '贷款利率', sortParam: INTEREST_RATES_SORT.phjrCd },
  { label: '贷款额度', sortParam: CREDIT_AMOUNT_SORT.phjrCd },
  { label: '贷款期限', sortParam: LOAN_TERMS_SORT.phjrCd }
]
const EMPTY_PRODUCT_FILTERS = {
  acceptMode: [],
  userType: [],
  payMode: [],
  productType: [],
  usageInf: []
}

const FixedOptions = ({ options, value, onChange }) => (
  <div className={styles['filter-row-options']}>
    <div
      className={`${styles['filter-option']} ${value.length === 0 ? styles['active'] : ''}`}
      onClick={() => onChange(null)}
    >不限</div>
    {options.map(option => (
      <div
        key={option.cd}
        className={`${styles['filter-option']} ${value.includes(option.phjrCd) ? styles['active'] : ''}`}
        onClick={() => onChange(option.phjrCd)}
      >
        {option.label}
      </div>
    ))}
  </div>
)

const SmallLoan = () => {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState(() => ({ ...EMPTY_PRODUCT_FILTERS }))
  const [sortDatas, setSortDatas] = useState([])

  useEffect(() => {
    let active = true
    setLoading(true)
    Loan.getProductListOnShelf(buildProductListRequest(current, pageSize, filters, sortDatas))
      .then(page => {
        if (!active) return
        setData((page?.content || []).map(normalizeProductCard))
        setTotal(page?.totalElements || 0)
      })
      .catch(err => {
        if (!active) return
        console.error('获取信贷产品列表失败', err)
        setData([])
        setTotal(0)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [current, pageSize, filters, sortDatas])

  const handleFilterChange = (field, value) => {
    const currentValues = filters[field]
    const nextValues = value === null
      ? []
      : currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value]

    if (nextValues.length > 10) {
      message.warning('每组筛选条件最多选择10项')
      return
    }
    if (nextValues.length === currentValues.length &&
        nextValues.every((item, index) => item === currentValues[index])) return

    setCurrent(1)
    setFilters(currentFilters => ({ ...currentFilters, [field]: nextValues }))
  }

  const handlePageChange = (page, size) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (size !== pageSize) {
      setCurrent(1)
      setPageSize(size)
      return
    }
    setCurrent(page)
  }

  /**
   * 按未排序、升序、降序、未排序循环切换，并保持首次激活顺序作为排序优先级。
   * 移除某项后数组自然前移，确保页面标记与接口 sortDatas 顺序一致。
   *
   * @param {'maxLoanTerms'|'minInterestRates'|'maxCreditAmount'} sortParam 排序字段
   */
  const handleSortChange = sortParam => {
    setCurrent(1)
    setSortDatas(currentSortDatas => {
      const sortIndex = currentSortDatas.findIndex(item => item.sortParam === sortParam)
      if (sortIndex === -1) {
        return currentSortDatas.length < 3
          ? [...currentSortDatas, { sortParam, direction: 'ASC' }]
          : currentSortDatas
      }
      if (currentSortDatas[sortIndex].direction === 'ASC') {
        return currentSortDatas.map((item, index) => index === sortIndex
          ? { ...item, direction: 'DESC' }
          : item)
      }
      return currentSortDatas.filter((item, index) => index !== sortIndex)
    })
  }

  const handleViewDetail = id => {
    window.open(`${window.location.origin}${window.location.pathname}#/small-loan/detail/${id}`, '_blank')
  }

  return (
    <div className={styles['small-loan']}>
      <div className={styles['filter-top-bar']}>
        <div className={styles['filter-top-content']}>
          <div className={styles['filter-tags']}>
            <span className={styles['filter-tags-label']}>筛选条件:</span>
            <div className={styles['filter-tags-bar']} />
          </div>
        </div>
      </div>

      <div className={styles['filter-panel']}>
        <div className={styles['filter-panel-content']}>
          <div className={styles['filter-row']}>
            <div className={styles['filter-row-label']}>贷款对象</div>
            <FixedOptions
              options={CUSTOMER_TYPE_OPTIONS}
              value={filters.userType}
              onChange={value => handleFilterChange('userType', value)}
            />
          </div>

          <div className={styles['filter-row']}>
            <div className={styles['filter-row-label']}>还款方式</div>
            <FixedOptions
              options={PRODUCT_ENUM_GROUPS.payModes}
              value={filters.payMode}
              onChange={value => handleFilterChange('payMode', value)}
            />
          </div>
          <div className={styles['filter-row']}>
            <div className={styles['filter-row-label']}>受理模式</div>
            <FixedOptions
              options={ACCEPT_MODE_OPTIONS}
              value={filters.acceptMode}
              onChange={value => handleFilterChange('acceptMode', value)}
            />
          </div>
          <div className={styles['filter-row']}>
            <div className={styles['filter-row-label']}>贷款用途</div>
            <FixedOptions
              options={PRODUCT_ENUM_GROUPS.usageInfs}
              value={filters.usageInf}
              onChange={value => handleFilterChange('usageInf', value)}
            />
          </div>
          <div className={styles['filter-row']}>
            <div className={styles['filter-row-label']}>产品类型</div>
            <FixedOptions
              options={PRODUCT_ENUM_GROUPS.productTypes}
              value={filters.productType}
              onChange={value => handleFilterChange('productType', value)}
            />
          </div>

          <div className={styles['sort-bar']}>
            {PRODUCT_SORT_OPTIONS.map(option => {
              const sortIndex = sortDatas.findIndex(item => item.sortParam === option.sortParam)
              const sortData = sortDatas[sortIndex]
              const directionLabel = sortData?.direction === 'ASC' ? '升序' : '降序'
              const ariaLabel = sortData
                ? `${option.label}，排序优先级${sortIndex + 1}，${directionLabel}`
                : `${option.label}，未排序`
              return (
                <button
                  key={option.sortParam}
                  type='button'
                  aria-label={ariaLabel}
                  className={`${styles['sort-item']} ${sortData ? styles['active'] : ''}`}
                  onClick={() => handleSortChange(option.sortParam)}
                >
                  <span>{option.label}</span>
                  {sortData && (
                    <>
                      <span className={styles['sort-priority']}>{sortIndex + 1}</span>
                      <span className={styles['sort-direction']} aria-hidden='true'>
                        {sortData.direction === 'ASC' ? '↑' : '↓'}
                      </span>
                    </>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className={styles['data-section']}>
        <div className={styles['data-content']}>
          {loading ? (
            <div className={styles['loading']}>加载中...</div>
          ) : (
            <>
              <div className={styles['card-grid']}>
                {data.map(item => (
                  <div key={item.productId} className={styles['data-card']}>
                    <div className={styles['card-header']}>
                      <div className={styles['bank-logo']}>
                        <div className={styles['logo-placeholder']}>
                          {item.orgName === '--' ? '银' : item.orgName.substring(0, 1)}
                        </div>
                      </div>
                      <div className={styles['bank-name']}>{item.orgName}</div>
                    </div>
                    <div className={styles['card-title']}>{item.productName || '--'}</div>
                    <div className={styles['card-info-grid']}>
                      <div className={styles['info-grid-item']}>
                        <div className={styles['info-label']}>最高额度</div>
                        <div className={styles['info-value']}>￥{item.amountText}</div>
                      </div>
                      <div className={styles['info-grid-item']}>
                        <div className={styles['info-label']}>最长期限</div>
                        <div className={styles['info-value']}>{item.termText}</div>
                      </div>
                      <div className={styles['info-grid-item']}>
                        <div className={styles['info-label']}>{item.ratesUnitDesc || ''}利率</div>
                        <div className={styles['info-value']}>{item.rateText}</div>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <Button
                        type='primary'
                        onClick={() => handleViewDetail(item.productId)}
                        className={styles['detail-btn']}
                      >
                        查看详情
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {total > 0 && (
                <div className={styles['pagination-wrapper']}>
                  <Pagination
                    current={current}
                    total={total}
                    pageSize={pageSize}
                    showSizeChanger
                    showQuickJumper
                    showTotal={count => `共 ${count} 条`}
                    onChange={handlePageChange}
                    pageSizeOptions={['12', '24', '48', '96']}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SmallLoan
